import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type PanelTone = 'accent' | 'good' | 'crit' | 'muted';

const TONE_CLASS: Record<PanelTone, string> = {
  accent: 'text-accent',
  good: 'text-status-good',
  crit: 'text-status-crit',
  muted: 'text-text-muted',
};

export interface PanelProps {
  /** The storyboard's `.h2` kicker — 11px, bold, uppercase, wide tracking. */
  title?: ReactNode;
  /** Kicker colour. The storyboard uses good/crit for moved vs stuck lists. */
  tone?: PanelTone;
  /** Right-aligned slot on the title row. */
  actions?: ReactNode;
  /** Drop the panel fill and use a hairline box instead (gantt, tables). */
  variant?: 'filled' | 'outlined';
  children?: ReactNode;
  className?: string;
}

/**
 * The storyboard's `.panelbox`: a section container inside a page. Filled
 * variant is the default `--color-panel` block; outlined is the hairline box
 * used where content supplies its own background (gantt, tables).
 */
export function Panel({
  title,
  tone = 'accent',
  actions,
  variant = 'filled',
  children,
  className,
}: PanelProps) {
  return (
    <section
      className={cn(
        'rounded-lg p-3',
        variant === 'filled' ? 'bg-panel' : 'border border-hairline bg-surface',
        className
      )}
    >
      {title === undefined && actions === undefined ? null : (
        <header className="mb-2 flex items-baseline justify-between gap-2">
          {title === undefined ? null : (
            <h2 className={cn('text-sm font-bold uppercase tracking-kicker', TONE_CLASS[tone])}>
              {title}
            </h2>
          )}
          {actions === undefined ? null : <div className="text-2xs text-text-muted">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export interface PageHeadingProps {
  title: ReactNode;
  /** The storyboard's right-hand explanatory line on the `.rowline`. */
  note?: ReactNode;
  className?: string;
}

/** The storyboard's `.rowline` + `.h1`: page title with a muted note. */
export function PageHeading({ title, note, className }: PageHeadingProps) {
  return (
    <div className={cn('mb-2.5 flex flex-wrap items-baseline justify-between gap-2.5', className)}>
      <h1 className="text-lg font-extrabold text-text-primary">{title}</h1>
      {note === undefined ? null : <p className="text-xs text-text-muted">{note}</p>}
    </div>
  );
}
